/**
 * Simplified fullscreen functionality for components
 * Hides all other components and displays only the target component in fullscreen mode
 */

import { useEffect, useState } from "react";

/**
 * Enter fullscreen mode for a specific component
 * Sets a query parameter to identify fullscreen state
 * @param {string} componentName - Name of component to display (e.g., 'today-trade-table')
 * @param {object} params - Additional query parameters to pass
 */
export const enterFullscreen = (componentName, params = {}) => {
    const queryParams = new URLSearchParams({
        fullscreen: componentName,
        ...params
    });
    window.history.pushState({}, "", `?${queryParams.toString()}`);
};

/**
 * Exit fullscreen mode and return to normal view
 */
export const exitFullscreen = () => {
    window.history.pushState({}, "", window.location.pathname);
};

/**
 * Check if currently in fullscreen mode
 * @param {string} componentName - Specific component name to check (optional)
 * @returns {boolean|string} Returns component name if in fullscreen, false otherwise
 */
export const isFullscreenMode = () => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('fullscreen') || null;
};

/**
 * React hook to track fullscreen state
 * @returns {string|null} Currently fullscreened component name or null
 */
export const useFullscreenMode = () => {
    const [fullscreenComponent, setFullscreenComponent] = useState(() => isFullscreenMode());

    useEffect(() => {
        const handlePopState = () => {
            setFullscreenComponent(isFullscreenMode());
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    return fullscreenComponent;
};

/**
 * Get additional fullscreen parameters from URL
 * @returns {object} Query parameters (excluding 'fullscreen' key)
 */
export const getFullscreenParams = () => {
    const params = new URLSearchParams(window.location.search);
    const result = {};
    params.forEach((value, key) => {
        if (key !== 'fullscreen') {
            result[key] = value;
        }
    });
    return result;
};